import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, Sparkles, MessageSquare, Award } from "lucide-react";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import ProductCard from "../../components/storefront/ProductCard";
import HeroBanner from "../../components/storefront/HeroBanner";
import { LOGO_URL, ytEmbed } from "../../lib/constants";
import { api } from "../../lib/api";
import { FAQSection, TestimonialsSection, BannerSlider } from "../../components/storefront/HomeSections";
import { ShowroomsGrid } from "./StaticPages";
import InstagramReels from "@/components/storefront/InstagramReels";
import frameImg from "../../aseets/fram.png";
import BudgetMatcherBar from "../../components/BudgetMatcherBar";

// Shown only until the admin-managed banners (kind="highlight") load from
// Site CMS — keeps this section from ever appearing empty.
const DEFAULT_HIGHLIGHTS = [
  { t: "Bridal", s: "The wedding archive", img: "https://images.unsplash.com/photo-1684868265714-fd2300637c23?w=900&q=85", to: "/catalog?occasion=Wedding" },
  { t: "Daily Wear", s: "Light, layered, loved", img: "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=900&q=85", to: "/catalog?occasion=Daily" },
  { t: "Gifting", s: "Made to be remembered", img: "https://images.unsplash.com/photo-1535632787350-4e68ef0ac584?w=900&q=85", to: "/catalog?occasion=Gifting" },
];



export default function Home() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [highlights, setHighlights] = useState(DEFAULT_HIGHLIGHTS);
  const [videoUrl, setVideoUrl] = useState("");
  const [scheme, setScheme] = useState(null);   // editable "Save in Gold" ribbon
  const [reels, setReels] = useState([]);
  const [instagramUrl, setInstagramUrl] = useState("");

  useEffect(() => {
    api.get("/api/reels").then((r) => { setReels(r.data?.reels || []); setInstagramUrl(r.data?.instagram_url || ""); }).catch(() => {});
    api.get("/api/products?is_new_arrival=true&limit=8").then((r) => setProducts(r.data.items || []));
    api.get("/api/products?is_bestseller=true&limit=4").then((r) => setBestsellers(r.data.items || []));
    api.get("/api/blogs").then((r) => setBlogs((r.data || []).slice(0, 3)));
    // Homepage highlight cards (Bridal / Daily Wear / Gifting) — admin-managed
    // via Site CMS → Banners (kind = "highlight"). Falls back to the default
    // three so the section is never empty while nothing's been added yet.
    api.get("/api/banners?kind=highlight").then((r) => { if ((r.data || []).length > 0) setHighlights(r.data); }).catch(() => {});
    // Home page video — set by Super Admin (or a delegated Major Admin)
    api.get("/api/site-settings").then((r) => setVideoUrl(r.data?.showroom_video_url || "")).catch(() => {});
    api.get("/api/site-content/home-scheme").then((r) => setScheme(r.data || null)).catch(() => {});
  }, []);


  return (
    <StorefrontLayout>
      {/* HERO — admin-managed banner carousel */}
      <HeroBanner />

      {/* Smart Budget Match — premium full-width banner, end to end */}
      <section data-testid="home-budget-banner">
        <BudgetMatcherBar />
      </section>

      {/* Trust strip — royal framed container */}
      {/* Trust strip — royal framed container */}
      <section className="px-6 lg:px-12 py-10">
        {/* Desktop / tablet: real ornate frame image as background */}
        <div
          className="hidden md:flex relative w-full items-center justify-center bg-no-repeat bg-center bg-contain reveal
                     [aspect-ratio:7/1] lg:[aspect-ratio:9/1] xl:[aspect-ratio:10/1]"
          style={{ backgroundImage: `url(${frameImg})` }}
        >
          <div className="grid grid-cols-4 gap-4 lg:gap-8 text-center w-[80%] px-6">
            {[
              { icon: <ShieldCheck size={20}/>, t: "BIS Hallmarked", s: "Every piece, certified" },
              { icon: <Award size={20}/>, t: "HUID Tagged", s: "Verifiable on BIS Care" },
              { icon: <Sparkles size={20}/>, t: "Lifetime Buyback", s: "Today's rate, transparent" },
              { icon: <MessageSquare size={20}/>, t: "Live Astrology", s: "2 free messages" },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-0.5 leading-none text-ash reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="text-gold">{item.icon}</div>
                <div className="font-display text-sm lg:text-lg text-maroon whitespace-nowrap">{item.t}</div>
                <div className="text-[9px] lg:text-[11px] tracking-wide whitespace-nowrap">{item.s}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: frame is too thin for a 2×2 grid, so use a simple gold-bordered panel */}
        <div className="md:hidden relative border border-gold/60 rounded-xl bg-white px-6 py-8">
          <div className="grid grid-cols-2 gap-6 text-center">
            {[
              { icon: <ShieldCheck size={22}/>, t: "BIS Hallmarked", s: "Every piece, certified" },
              { icon: <Award size={22}/>, t: "HUID Tagged", s: "Verifiable on BIS Care" },
              { icon: <Sparkles size={22}/>, t: "Lifetime Buyback", s: "Today's rate, transparent" },
              { icon: <MessageSquare size={22}/>, t: "Live Astrology", s: "2 free messages" },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2 text-ash reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="text-gold">{item.icon}</div>
                <div className="font-display text-lg text-maroon leading-none">{item.t}</div>
                <div className="text-[11px] tracking-wide">{item.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story + video */}
<section className="px-6 lg:px-12 py-16">
  <div className="grid lg:grid-cols-2 gap-10 items-center">
    <div className="reveal">
      <div className="eyebrow">Our craft</div>
      <h2 className="font-display text-4xl lg:text-5xl text-maroon mt-2">Where every piece begins</h2>
      <p className="text-ash mt-4 leading-relaxed max-w-md">
        From the karigar's bench to your hands — each Swayambhu Gold creation passes through
        generations of skill, BIS hallmarking, and a story worth carrying forward. Watch how
        an heirloom takes shape.
      </p>
      <Link to="/about" className="btn-maroon mt-8 inline-flex" data-testid="cta-our-story">
        Our Story <ArrowRight size={14} />
      </Link>
    </div>
    <div className="reveal">
      <div className="aspect-video rounded-xl overflow-hidden bg-pearl border border-line">
        {ytEmbed(videoUrl, { loop: true }) ? (
          <iframe
            src={ytEmbed(videoUrl, { loop: true })}
            title="Swayambhu Gold craft"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="w-full h-full"
            data-testid="story-video"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ash text-sm text-center px-6" data-testid="story-video-empty">
            Our craft film is coming soon.
          </div>
        )}
      </div>
    </div>
  </div>
</section>

      {/* New arrivals */}
      <section className="px-6 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-10 reveal">
          <div>
            <div className="eyebrow">Just in</div>
            <h2 className="font-display text-4xl lg:text-5xl text-maroon mt-1">{t("home.newArrivals")}</h2>
          </div>
          <Link to="/catalog" className="inline-flex eyebrow hover:text-gold shrink-0" data-testid="link-view-all-new">{t("common.viewAll")} →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 rounded-xl gap-3 sm:gap-5">
          {products.slice(0, 8).map((p, i) => (
            <div key={p.id} className="reveal min-w-0" style={{ transitionDelay: `${i * 70}ms` }}>
              <ProductCard product={p} />
            </div>
          ))}
        </div>
      </section>

      {/* Category showcase — image, title, subtitle & link all admin-managed
          via Site CMS → Banners (kind="highlight"); change/delete anytime. */}
      <section className="px-6 lg:px-12 py-8">
        <div className="royal-divider mb-10" />
        <div className="grid lg:grid-cols-3 gap-5">
          {highlights.map((c, i) => (
            <Link key={c.id || c.t} to={c.cta_link || c.to} className="group relative aspect-[4/5] block overflow-hidden rounded-xl reveal" style={{ transitionDelay: `${i * 100}ms` }} data-testid={`category-tile-${(c.title || c.t || "").toLowerCase()}`}>
              <img src={c.image_url || c.img} alt={c.title || c.t} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-maroon-dark/80 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 text-pearl">
                <div className="eyebrow text-gold-light">{c.subtitle || c.s}</div>
                <div className="font-display text-3xl">{c.title || c.t}</div>
                <div className="mt-2 text-xs underline group-hover:text-gold-light">{c.cta_text || "Explore"} →</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Bestsellers */}
      {bestsellers.length > 0 && (
        <section className="px-6 lg:px-12 py-20 bg-gradient-to-b from-pearl to-white">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow">From our archive</div>
            <h2 className="font-display text-4xl lg:text-5xl text-maroon mt-1">{t("home.bestsellers")}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {bestsellers.map((p, i) => (
              <div key={p.id} className="reveal min-w-0" style={{ transitionDelay: `${i * 70}ms` }}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/catalog" className="btn-outline inline-flex items-center gap-2" data-testid="link-view-catalog-bestsellers">
              View full catalog <ArrowRight size={14}/>
            </Link>
          </div>
        </section>
      )}

      {/* Gold programs ribbon — fully editable from the dashboard */}
      {scheme && (scheme.title || scheme.body) && (
      <section className="px-6 lg:px-12 py-12">
      <div className="bg-maroon text-pearl px-8 lg:px-16 py-10 grid lg:grid-cols-2 gap-10 items-center royal-frame reveal rounded-2xl overflow-hidden">
        <div>
          {scheme?.eyebrow && <div className="eyebrow text-gold-light">{scheme.eyebrow}</div>}
          <h2 className="font-display text-4xl lg:text-5xl mt-2">{scheme?.title || ""}</h2>
          {scheme?.body && <p className="mt-4 text-pearl/80 max-w-md">{scheme.body}</p>}
          {scheme?.cta_label && (
            <Link to={scheme.cta_link || "/gold-programs"} className="btn-gold mt-8 inline-flex" data-testid="cta-golden-harvest">
              {scheme.cta_label} <ArrowRight size={14}/>
            </Link>
          )}
        </div>
        <div className="relative">
          {scheme?.hero_image_url && (
            <img src={scheme.hero_image_url} alt={scheme?.title || "Gold programs"} className="aspect-[4/3] object-cover w-full rounded-xl" />
          )}
        </div>
      </div>
    </section>
      )}

      {/* Swayambhu Gold Scheme — invest monthly, lock the day's price */}
      <section className="px-6 lg:px-12 pb-4">
        <div className="bg-gradient-to-r from-gold/20 to-maroon/10 border border-gold rounded-2xl px-5 sm:px-8 lg:px-14 py-8 sm:py-10 grid lg:grid-cols-2 gap-8 items-center reveal">
          <div>
            <div className="eyebrow text-maroon">New · Swayambhu Gold Scheme</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-maroon mt-2">Invest monthly, lock today's gold price</h2>
            <p className="mt-3 text-ash max-w-md text-sm">
              Put in a flexible amount every month for 12 months — you choose how much (minimum ₹5,000).
              Whatever you pay buys gold at that day's rate and is credited to you. At the end, take your
              accumulated gold and we waive the making charges.
            </p>
            <Link to="/gold-scheme" className="btn-maroon mt-6 inline-flex" data-testid="cta-gold-scheme">
              Explore the scheme <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
            {[["12", "monthly instalments"], ["Flexible", "amount you choose"], ["Free", "making at the end"]].map(([a, b], i) => (
              <div key={i} className="bg-maroon border border-gold rounded-xl py-4 sm:py-5 px-1 sm:px-2">
                <div className="font-display text-base sm:text-2xl text-gold-light leading-tight break-words">{a}</div>
                <div className="text-[9px] sm:text-[10px] text-gold/90 uppercase tracking-wider sm:tracking-widest mt-1 leading-tight">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blogs */}
      {blogs.length > 0 && (
        <section className="px-6 lg:px-12 py-20">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow">Blogs</div>
            <h2 className="font-display text-4xl lg:text-5xl text-maroon mt-1">{t("home.fromJournal")}</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            {blogs.map((b, i) => (
              <Link key={b.id} to={`/blog/${b.slug}`} className="group block reveal" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`blog-tile-${b.slug}`}>
                <div className="zoom-frame aspect-[4/3] bg-pearl border border-line rounded-xl overflow-hidden">
                  <img src={b.cover_image || "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=900&q=85"} alt={b.title} className="w-full h-full object-cover" />
                </div>
                <div className="mt-4">
                  <div className="eyebrow text-[10px]">{(b.tags || []).join(" · ")}</div>
                  <div className="font-display text-2xl text-maroon group-hover:text-gold-dark transition-colors">{b.title}</div>
                  <p className="text-sm text-ash mt-1 line-clamp-2">{b.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-sm text-maroon font-medium mt-2 group-hover:gap-2 transition-all" data-testid={`blog-readmore-${b.slug}`}>Read Blog <ArrowRight size={14}/></span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Instagram Reels — managed from Site CMS */}
      <InstagramReels reels={reels} instagramUrl={instagramUrl} />

      {/* Visit a store */}
      <section className="px-6 lg:px-12 py-20 bg-pearl">
        <div className="text-center reveal mb-10">
          <img src={LOGO_URL} alt="Swayambhu Gold" className="h-16 mx-auto mb-6 animate-float" />
          <div className="eyebrow">Two showrooms, one promise</div>
          <h2 className="font-display text-4xl lg:text-5xl text-maroon mt-2">{t("home.visitStore")}</h2>
          <p className="text-ash mt-3 max-w-xl mx-auto">Belagavi · Khanapur — walk in, sit with a story, and find a piece that finds you.</p>
        </div>
        <ShowroomsGrid />
      </section>

      <BannerSlider />
      {/* Note: the craft-section video above is the single admin-managed home
          video. <ShowroomVideo /> reads the same setting, so rendering it here
          too would show the same film twice. */}
      <TestimonialsSection />
      <FAQSection />
    </StorefrontLayout>
  );
}